import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import { CATEGORIES, categoryLabel } from "../lib/categories";
import { ListingCard } from "../components/ListingCard";
import { Button, Input, EmptyState } from "../components/ui";
import { FirstRun } from "../../../shell/FirstRun";

export function BrowsePage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // debounced
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ status: "active" });
    if (category !== "all") params.set("category", category);
    if (query) params.set("q", query);
    apiGet(`/listings?${params.toString()}`)
      .then((data) => {
        if (!cancelled) setListings(data.listings || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category, query]);

  return (
    <div>
      <div className="mb-4">
        <FirstRun id="gear_browse" title="Welcome to the Gear Exchange" tone="slate">
          <p>
            Buy and sell used gear with other SSA families —{" "}
            <strong>there are no payments in the app</strong>. You arrange the handover
            between yourselves.
          </p>
          <p>
            <Link to="/help/gear" className="font-semibold underline">
              Visit the Help page
            </Link>{" "}
            for how it works, staying safe when you meet up, and setting up alerts.
          </p>
        </FirstRun>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gear Exchange</h1>
          <p className="text-sm text-slate-500">Buy & sell soccer gear with fellow SSA members.</p>
        </div>
        <Button onClick={() => navigate("/gear/sell")} className="shrink-0">
          <span className="hidden sm:inline">List an item</span>
          <span className="sm:hidden">Sell</span>
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search cleats, jerseys, sizes…"
        />
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Chip active={category === "all"} onClick={() => setCategory("all")}>
          All
        </Chip>
        {CATEGORIES.map((c) => (
          <Chip key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
            {c.label}
          </Chip>
        ))}
      </div>

      {/* Standing offer to be told about future matches, shown once the member
          has narrowed to something specific. */}
      {category !== "all" && (
        <div className="-mt-2 mb-4">
          <button
            onClick={() => navigate(`/gear/alerts?category=${encodeURIComponent(category)}`)}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Email me when {categoryLabel(category)} are listed
          </button>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square bg-slate-200 rounded-xl" />
              <div className="h-4 bg-slate-200 rounded mt-2 w-3/4" />
              <div className="h-3 bg-slate-100 rounded mt-2 w-1/2" />
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState title="Couldn't load listings">
          {error}
        </EmptyState>
      ) : listings.length === 0 ? (
        <EmptyState title="Nothing here yet">
          {query || category !== "all"
            ? "Try a different category or search."
            : "Be the first to list some gear!"}
          {/* Looking and finding nothing is exactly when an alert is worth
              offering — otherwise they leave and never come back. */}
          <div className="mt-3">
            <Button
              variant="secondary"
              onClick={() =>
                navigate(
                  category !== "all"
                    ? `/gear/alerts?category=${encodeURIComponent(category)}`
                    : "/gear/alerts"
                )
              }
            >
              Notify me when something is listed
            </Button>
          </div>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {listings.map((l) => (
            <ListingCard key={l.id} listing={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ active, children, ...props }) {
  return (
    <button
      className={[
        "whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium border transition",
        active
          ? "bg-brand-600 text-white border-brand-600"
          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  );
}
